import { ColDef } from '@ag-grid-community/core';
import { IDetailCellRendererParams } from '@ag-grid-enterprise/master-detail/dist/es6/masterDetail/detailCellRenderer';
import { capitalize, find } from 'lodash';
import { toJS } from 'mobx';
import { Instance } from 'mobx-state-tree';
import moment from 'moment';
import Router from 'next/router';

import ImageRenderer from '@/components/ImageRenderer';
import getGroupCellRenderer from '@/components/Table/GroupCellRenderer';
import SelectAllHeader from '@/components/Table/SelectAllHeader';
import VisibilityCellRenderer from '@/components/Table/VisibilityCellRenderer';
import { Product, ProductVariant } from '@/services/product/types';
import { AllProductsPage } from '@/stores/all-products-page';

interface ProductWithVariantsArray extends Omit<Product, 'variants'> {
  variants: ProductVariant[];
}

const formatVariantsForDetailGrid = (product: ProductWithVariantsArray) => {
  const { legacyProductsId, productId, variants } = product;

  return variants?.map((variant: ProductVariant) => ({
    image: find(variant.images, ['position', 0]) || {},
    legacyProductsId,
    price: formatPrice(variant.prices?.[0]?.amount),
    productId,
    quantity: variant.quantity,
    salePrice: formatPrice(variant.prices?.[0]?.saleAmount),
    sku: capitalize(variant.sku),
    state: variant.isShopperVisible ? 'Active' : '',
    updatedAt: moment(variant.updatedAt).fromNow(),
    variantId: variant.variantId,
  }));
};

const getDetailCellRendererParams = (
  newProductEdit: boolean
): Partial<IDetailCellRendererParams> => ({
  detailGridOptions: {
    columnDefs: detailGridColumnDefs,
    defaultColDef: {
      editable: false,
      resizable: false,
      sortable: false,
      suppressMovable: true,
    },
    frameworkComponents: { imageRenderer: ImageRenderer },
    rowStyle: {
      backgroundColor: '#F4F2FA',
      cursor: 'pointer',
    },
    onRowClicked: ({ data }) => {
      if (newProductEdit) {
        Router.push(
          `/admin/products/[productId]`,
          `/admin/products/${data.productId}`
        );
      } else {
        window.location.href = `${process.env.NEXT_PUBLIC_OLD_ADMIN_BASE_URI}/product/${data.legacyProductsId}/detail`;
      }
    },
    suppressCellSelection: true,
    suppressRowClickSelection: true,
  },
  getDetailRowData: ({ node, successCallback }) => {
    const product = toJS(node.data);
    successCallback(formatVariantsForDetailGrid(product));
  },
  // Simplest way to change template. Replacing with a styled component is an option, but involved.
  // The ref="eDetailGrid" element is what AgGrid will use to attach the detail grid.
  template: (params): string => {
    const variantCount = params.data.variants.length;
    const hasALot = variantCount >= 100;
    const moreVariantsMsg = hasALot
      ? 'There are more child products not displayed'
      : '';
    return `
    <div style="height: 100%; padding: 10px; background-color: #FBFAFE;">
      <div style="display: flex; justify-content: space-between;">
        <span style="font-weight: bold;">${variantCount}${
      hasALot ? '+' : ''
    } child products</span>
        <span style="font-style: italic;">${moreVariantsMsg}</span>
      </div>
      <div ref="eDetailGrid" style="height: 100%;"></div>
    </div>
    `;
  },
});

const handleGroupButtonClick = (id: string): void => {
  window.location.href = `${process.env.NEXT_PUBLIC_OLD_ADMIN_BASE_URI}/product/${id}/detail`;
};

const getMasterGridColumnDefs = (allProductsSelected: boolean): ColDef[] => [
  {
    checkboxSelection: true,
    colId: 'checkbox',
    field: 'checkbox',
    headerComponentFramework: SelectAllHeader,
    headerComponentParams: {
      allSelected: allProductsSelected,
    },
    headerName: '',
    maxWidth: 75,
    cellClass: 'product-row-checkbox',
  },
  {
    cellRendererFramework: VisibilityCellRenderer,
    colId: 'state',
    field: 'state',
    headerName: '',
    maxWidth: 50,
    resizable: false,
    sortable: false,
  },
  {
    cellRenderer: 'imageRenderer',
    colId: 'image',
    field: 'image',
    filter: false,
    headerName: '',
    maxWidth: 50,
    resizable: false,
    sortable: false,
    suppressMovable: true,
  },
  {
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: getGroupCellRenderer((value: string) =>
        handleGroupButtonClick(value)
      ),
    },
    colId: 'productId',
    field: 'productId',
    headerName: 'Sku',
    minWidth: 250,
  },
  {
    colId: 'name',
    field: 'name',
    filter: 'agTextColumnFilter',
    headerName: 'Name',
    maxWidth: 250,
  },
  {
    colId: 'quantity',
    field: 'quantity',
    headerName: 'Quantity',
    maxWidth: 125,
    sortable: false,
    type: 'numericColumn',
  },
  {
    cellClass: 'number-cell',
    colId: 'price',
    field: 'price',
    headerName: 'Price',
    maxWidth: 150,
    sortable: true,
  },
  {
    cellClass: 'number-cell',
    colId: 'salePrice',
    field: 'salePrice',
    headerName: 'Sale Price',
    maxWidth: 150,
    sortable: true,
  },
  {
    colId: 'categoriesList',
    field: 'categoriesList',
    headerName: 'Categories',
    sortable: false,
  },
  {
    colId: 'formattedUpdatedAt',
    field: 'formattedUpdatedAt',
    headerName: 'Last modified',
  },
  {
    colId: 'formattedWeight',
    field: 'formattedWeight',
    headerName: 'Weight',
    maxWidth: 150,
  },
  {
    colId: 'formattedCreatedAt',
    field: 'formattedCreatedAt',
    headerName: 'Date Added',
    maxWidth: 125,
  },
  {
    colId: 'pagePath',
    field: 'pagePath',
    headerName: 'Page Path',
    sortable: false,
  },
  {
    colId: 'seo.metaTitle',
    field: 'seo.metaTitle',
    headerName: 'SEO Title',
    sortable: false,
  },
  {
    colId: 'seo.metaDescription',
    field: 'seo.metaDescription',
    headerName: 'SEO Description',
    sortable: false,
  },
];

const formatPrice = (price?: number | null): string => {
  if (!price) return '-';
  const adjustedPrice = price ? price / 100 : 0;
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(adjustedPrice);
};

const detailGridColumnDefs: ColDef[] = [
  {
    cellRendererFramework: VisibilityCellRenderer,
    colId: 'state',
    field: 'state',
    headerName: '',
    maxWidth: 50,
  },
  {
    cellRenderer: 'imageRenderer',
    colId: 'image',
    field: 'image',
    filter: false,
    headerName: '',
    maxWidth: 50,
  },
  {
    colId: 'sku',
    field: 'sku',
    flex: 1,
    headerName: 'Sku',
    minWidth: 250,
  },
  {
    colId: 'quantity',
    field: 'quantity',
    headerName: 'Quantity',
    maxWidth: 150,
    type: 'numericColumn',
  },
  {
    cellClass: 'number-cell',
    colId: 'price',
    field: 'price',
    headerName: 'Price',
    maxWidth: 150,
  },
  {
    cellClass: 'number-cell',
    colId: 'salePrice',
    field: 'salePrice',
    headerName: 'Sale Price',
    maxWidth: 150,
  },
  {
    colId: 'updatedAt',
    field: 'updatedAt',
    headerName: 'Last modified',
    maxWidth: 300,
  },
];

interface MasterDetailGridOptions {
  detailCellRendererParams: Partial<IDetailCellRendererParams>;
  masterGridColumnDefs: ColDef[];
}

export const buildGridOptions = (
  allProductsPage: Instance<typeof AllProductsPage>,
  newProductEdit: boolean
): MasterDetailGridOptions => {
  const { allProductsSelected, getVisibleColumnDefinitions } = allProductsPage;
  const masterGridColumnDefs = getMasterGridColumnDefs(allProductsSelected);
  const visibleMasterGridColumnDefs =
    getVisibleColumnDefinitions(masterGridColumnDefs);

  return {
    detailCellRendererParams: getDetailCellRendererParams(newProductEdit),
    masterGridColumnDefs: visibleMasterGridColumnDefs,
  };
};
